import React from 'react';
import { connect } from 'react-redux';
import { Collapse, FormGroup, Radio } from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import classNames from 'clsx';
import ChevronDown from 'mdi-material-ui/ChevronDown';
import ChevronUp from 'mdi-material-ui/ChevronUp';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';
import isEqual from 'lodash-es/isEqual';
import get from 'lodash-es/get';

import { ControlContainer } from 'app/components/ControlContainer';
import { CurrencyFormatted } from 'app/components/CurrencyFormatted';
import {
  LargeRadioGroupProps,
  LargeRadioGroupState,
} from './LargeRadioGroup.interfaces';
import { DictionaryEntry, DictionaryEntryKey } from 'app/services/dict.service';
import { CustomEventsNameEnum, LabelEnum } from 'app/shared/enums';
import {
  addToInstallmentScheduleItem,
  formatInstallmentScheduleItem,
  pluralizeInstallments,
} from 'app/shared/helpers/installmentSchedule';
import { RootState } from 'app/store/rootReducer';
import {
  selectSelectedOffer,
  selectSelectedOfferInsurerName,
  selectSelectedOfferInternalInstallmentsEstimation,
} from 'app/store/data/selectedOffer';
import { selectMerchCapPriceAddition } from 'app/store/selectors/selectMerchCapPriceAddition';
import { selectIsPaymentMethodOnInstallmentsActive } from 'app/store/data/quotationResult/selectors';
import { selectPriceBeforeRecalculation } from 'app/store/data/priceBeforeRecalculation';
import { MubiInstallmentsRadioOption } from 'app/components/MubiInstallmentsRadioOption';
import { SelectWithFilter } from 'app/components/SelectWithFilter';
import { addToFormattedPrice } from 'app/shared/helpers/addToFormattedPrice';
import { getInsurerDisplayName } from 'app/shared/helpers/getInsurerDisplayName';

// Backend returns installments dictionary with same "key": "1" for one payment option and mubi (hiPRO) installments.
// For recognize which option is selected for example in business functions,
// key for mubi installments is changed to the following value.
// (Change is made only for frontend purposes, quotation is still patching with the original key).
const mubiInstallmentsKey = 'hipro';
const onePaymentKey = '1';

class LargeRadioGroup extends React.Component<
  LargeRadioGroupProps,
  LargeRadioGroupState
> {
  constructor(props: LargeRadioGroupProps) {
    super(props);
    this.state = {
      showTimetable: false,
      selectedNumberOfInstallments: null,
      mubiInstallments: null,
      noInstallments: false,
      oldInstallments: undefined,
      insurersDictionary: [],
      mubiInstallmentsRrsoExpanded: false,
    };
  }

  handleValueChange = (value: DictionaryEntryKey) => {
    const { input, isLoadingResult, onValueChange } = this.props;

    if (isLoadingResult) {
      return;
    }

    if (input.value !== value) {
      input.onChange(value);

      if (onValueChange) {
        const valueToPatch = value === mubiInstallmentsKey ? 1 : value;

        onValueChange(valueToPatch, true);
      }

      const event = new CustomEvent(
        CustomEventsNameEnum.MubiInstallmentsApplicationNotFilledError,
        {
          detail: {
            showError: false,
          },
        }
      );
      document.dispatchEvent(event);
    }
  };

  handleClick = () => {
    const { onClick } = this.props;

    if (onClick) {
      onClick();
    }
  };

  componentDidUpdate(
    prevProps: LargeRadioGroupProps,
    prevState: LargeRadioGroupState
  ) {
    if (!isEqual(prevProps.dictionary, this.props.dictionary)) {
      this.setState({ oldInstallments: prevProps.dictionary });
      this.setInstallments();
    }

    if (this.state.noInstallments && !this.isOnePaymentOptionSelected()) {
      const onePaymentEntry = this.getInstallmentByKey(onePaymentKey);
      if (onePaymentEntry) {
        this.handleValueChange(onePaymentEntry.key);
      }
    }

    if (
      this.props.isLoading ||
      prevState.selectedNumberOfInstallments ||
      prevState.mubiInstallments
    ) {
      return;
    }

    this.setInstallments();
  }

  render() {
    const {
      isLoading,
      width,
      classes,
      dictionary,
      isPaymentMethodOnInstallmentsActive,
    } = this.props;

    return (
      <ReactPlaceholder
        type={'text'}
        showLoadingAnimation
        rows={6}
        ready={!isLoading && !!dictionary.length}
        className={classes.placeholder}
      >
        <ControlContainer
          width={width}
          inputId={this.props.input.name}
          formName={this.props.formName}
          withoutHtmlFor
          isPaymentMethodOnInstallmentsActive={
            isPaymentMethodOnInstallmentsActive
          }
        >
          <FormGroup
            onClick={this.handleClick}
            className={classNames(classes.formGroup, {
              [classes.newStyleFormGroup]: isPaymentMethodOnInstallmentsActive,
            })}
            aria-labelledby={`payment.numberOfInstallments-label`}
          >
            {this.renderOnePaymentOption()}
            {this.renderInstallmentsOption()}
            {this.renderMubiInstallmentsOption()}
          </FormGroup>
        </ControlContainer>
      </ReactPlaceholder>
    );
  }

  private setInstallments = () => {
    const { noInstallments } = this.state;
    const { internalInstallmentsEstimation, dictionary } = this.props;

    const installments = this.getDefaultNumberOfInstallments();
    const mubiInstallments = this.getMubiInstallments();
    const hasMubiInstallmentsAvailable =
      mubiInstallments && !internalInstallmentsEstimation?.rejected;

    if (installments) {
      this.setState({
        selectedNumberOfInstallments: installments,
      });
    }

    if (mubiInstallments) {
      this.setState({
        mubiInstallments: mubiInstallments,
      });
    }

    if (
      !!dictionary.length &&
      !installments &&
      !hasMubiInstallmentsAvailable &&
      !noInstallments
    ) {
      this.setState({ noInstallments: true });
    }
  };

  private isInstallment = ({ key }: DictionaryEntry) =>
    key.toString() !== onePaymentKey;

  private getDefaultNumberOfInstallments = () => {
    const { input, dictionary } = this.props;

    if (input.value && !this.isOnePaymentOptionSelected()) {
      return this.getInstallmentByKey(input.value);
    }

    return dictionary
      .filter(this.isInstallment)
      .sort((first, second) =>
        first.key
          .toString()
          .localeCompare(second.key.toString(), undefined, { numeric: true })
      )[0];
  };

  private getMubiInstallments = () => {
    const { dictionary } = this.props;

    return dictionary.find(
      (entry) => entry.installmentsInfo?.internalInstallments
    );
  };

  private handleNumberOfInstallmentsChange = (selectedKey: string) => {
    this.handleValueChange(selectedKey);
    const selectedNumberOfInstallments = this.getInstallmentByKey(
      selectedKey.toString()
    );
    if (selectedNumberOfInstallments) {
      this.setState({
        selectedNumberOfInstallments,
      });
    }
  };

  private isOnePaymentOptionSelected = () =>
    this.props.input.value.toString() === onePaymentKey;

  private isMubiPaymentOptionSelected = () =>
    this.props.input.value.toString() === mubiInstallmentsKey;

  private renderInstallmentsOption() {
    const {
      classes,
      input: { value },
      insurerName,
      isPaymentMethodOnInstallmentsActive,
    } = this.props;

    const { showTimetable, selectedNumberOfInstallments } = this.state;

    if (selectedNumberOfInstallments === null) {
      return null;
    }

    const { installmentsInfo } = selectedNumberOfInstallments;
    const installments = installmentsInfo && installmentsInfo.installments;

    const checked =
      !!value &&
      !this.isOnePaymentOptionSelected() &&
      !this.isMubiPaymentOptionSelected();

    return (
      <div
        tabIndex={0}
        className={classNames(classes.controlContainer, 'focusVisibleOutline', {
          [classes.active]: checked,
          [classes.error]: this.hasError(),
        })}
        onClick={() => this.handleValueChange(selectedNumberOfInstallments.key)}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            this.handleValueChange(selectedNumberOfInstallments.key);
          }
        }}
      >
        {this.renderRadio(
          checked,
          `${LabelEnum.InstallmentsFrom} ${getInsurerDisplayName(insurerName)}`
        )}
        <div className={classes.installmentsDetails}>
          <div className={classes.leftColumn}>
            <div
              className={
                isPaymentMethodOnInstallmentsActive
                  ? classes.greyBoxHighlightedNew
                  : classes.greyBoxHighlighted
              }
            >
              {`${LabelEnum.Sum}: ${this.getPriceWithMerchCap(
                selectedNumberOfInstallments.value
              )}`}
            </div>
            {this.installments.length === 1
              ? this.renderOnlyOptionInfo()
              : this.renderNumberOfInstallmentsSelect()}
            {this.renderRecalculatedInstallmentInfo()}
          </div>
          <span
            className={classNames(classes.timetable, 'focusVisibleOutline')}
            onClick={this.changeExpanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (
                e.target === e.currentTarget &&
                (e.key === 'Enter' || e.key === ' ')
              ) {
                e.preventDefault();
                this.changeExpanded();
              }
            }}
          >
            {LabelEnum.Timetable}
            {this.state.showTimetable ? <ChevronUp /> : <ChevronDown />}
          </span>
        </div>
        <Collapse in={showTimetable}>
          {installments && (
            <div className={classes.additionalInfoContainer}>
              {installments.map((installment, index) => {
                const scheduleItem = this.getScheduleItemWithMerchCap(
                  installment,
                  index
                );

                return isPaymentMethodOnInstallmentsActive ? (
                  <span key={installment}>
                    {formatInstallmentScheduleItem(scheduleItem)}
                  </span>
                ) : (
                  <div key={installment}>{scheduleItem}</div>
                );
              })}
            </div>
          )}
        </Collapse>
      </div>
    );
  }

  private renderMubiInstallmentsOption() {
    const {
      input: { value },
    } = this.props;
    const { mubiInstallments } = this.state;

    if (mubiInstallments === null) {
      return null;
    }

    const checked = !!value && this.isMubiPaymentOptionSelected();

    return (
      <MubiInstallmentsRadioOption
        checked={checked}
        mubiInstallments={mubiInstallments}
        hasError={this.hasError()}
        onClick={() => this.handleValueChange(mubiInstallmentsKey)}
      />
    );
  }

  private getRecalculatedInstallmentOldPrice = (): number | null => {
    const { priceBeforeRecalculation, selectedOffer } = this.props;
    const { selectedNumberOfInstallments } = this.state;

    if (
      !selectedNumberOfInstallments ||
      !selectedOffer ||
      priceBeforeRecalculation.oneTime === null
    ) {
      return null;
    }

    const isOneTimePriceUnchanged =
      priceBeforeRecalculation.oneTime === selectedOffer.price.finalPrice;

    if (!isOneTimePriceUnchanged) {
      return null;
    }

    const numberOfInstallments = Number(selectedNumberOfInstallments.key);
    const oldInstallmentPrice =
      priceBeforeRecalculation.installments[numberOfInstallments];
    const newInstallmentPrice = get(
      selectedOffer,
      `price${numberOfInstallments}installments.finalPrice`
    ) as number | undefined;

    if (
      oldInstallmentPrice === undefined ||
      newInstallmentPrice === undefined ||
      oldInstallmentPrice === newInstallmentPrice
    ) {
      return null;
    }

    return oldInstallmentPrice;
  };

  private renderRecalculatedInstallmentInfo = () => {
    const { classes } = this.props;
    const oldInstallmentPrice = this.getRecalculatedInstallmentOldPrice();

    if (oldInstallmentPrice === null) {
      return null;
    }

    return (
      <div className={classes.rateChangedInfo}>
        {LabelEnum.InstallmentsPriceRecalculatedPrefix}
        <CurrencyFormatted quantity={oldInstallmentPrice} />
        {LabelEnum.InstallmentsPriceRecalculatedSuffix}
      </div>
    );
  };

  private renderOnlyOptionInfo = () => {
    const { classes, isPaymentMethodOnInstallmentsActive } = this.props;
    const installment = this.installments[0];
    return (
      <div
        className={
          isPaymentMethodOnInstallmentsActive
            ? classes.greyBoxNew
            : classes.greyBox
        }
      >
        {LabelEnum.NumberOfInstallments}: {installment.key}
      </div>
    );
  };

  private renderNumberOfInstallmentsSelect = () => {
    const { classes, isPaymentMethodOnInstallmentsActive } = this.props;
    const { selectedNumberOfInstallments } = this.state;

    return (
      <SelectWithFilter
        withoutStartAdornment
        focused={false}
        onClick={(e) => e.stopPropagation()}
        className={classNames(classes.installmentsCountSelect, {
          [classes.withMarginLeft]: isPaymentMethodOnInstallmentsActive,
        })}
        dictionary={this.installments}
        label={LabelEnum.NumberOfInstallments}
        name={'number-of-installments'}
        onChange={this.handleNumberOfInstallmentsChange}
        value={
          selectedNumberOfInstallments
            ? selectedNumberOfInstallments.key.toString()
            : ''
        }
      />
    );
  };

  private getPriceWithMerchCap = (formattedPrice: string) =>
    addToFormattedPrice(formattedPrice, this.props.merchCapPriceAddition);

  private getScheduleItemWithMerchCap = (item: string, index: number) =>
    index === 0
      ? addToInstallmentScheduleItem(item, this.props.merchCapPriceAddition)
      : item;

  private changeExpanded = () =>
    this.setState({ showTimetable: !this.state.showTimetable });

  private renderRadio = (checked: boolean, label: string | undefined) => {
    const { classes } = this.props;

    return (
      <div className={classes.radio} data-cy="installments-payment">
        <Radio
          tabIndex={-1}
          color={'primary'}
          checked={checked}
          checkedIcon={<RadioboxMarked />}
        />
        <label>{label}</label>
      </div>
    );
  };

  private getInstallmentByKey = (keyToFind: string) =>
    this.props.dictionary.find(
      (entry) =>
        keyToFind === entry.key.toString() &&
        !entry.installmentsInfo?.internalInstallments
    );

  private renderOnePaymentOption() {
    const { classes } = this.props;
    const { noInstallments, oldInstallments } = this.state;
    const onePaymentEntry = this.getInstallmentByKey(onePaymentKey);

    if (!onePaymentEntry) {
      return null;
    }

    let oldValue;

    if (oldInstallments) {
      oldValue = oldInstallments.find(
        (installment) => !this.isInstallment(installment)
      )?.value;
    }

    const { key, value } = onePaymentEntry;

    const checked = this.isOnePaymentOptionSelected() || noInstallments;

    return (
      <div
        tabIndex={0}
        className={classNames(classes.controlContainer, 'focusVisibleOutline', {
          [classes.active]: this.isOnePaymentOptionSelected(),
          [classes.error]: this.hasError(),
        })}
        data-cy="one-payment"
        onClick={() => this.handleValueChange(key)}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            this.handleValueChange(key);
          }
        }}
      >
        {this.renderRadio(checked, LabelEnum.OneTimePayment)}
        <div className={classes.checkWithPriceContainer}>
          {oldValue && <span className={classes.oldPrice}>{oldValue}</span>}
          {this.getPriceWithMerchCap(value)}
        </div>
      </div>
    );
  }

  private hasError() {
    const { meta } = this.props;
    return meta.touched && !meta.valid;
  }

  private get installments() {
    const { dictionary, isPaymentMethodOnInstallmentsActive } = this.props;

    return dictionary.filter(this.isInstallment).map((item) => ({
      key: item.key,
      value: isPaymentMethodOnInstallmentsActive
        ? `${item.key} ${pluralizeInstallments(Number(item.key))}`
        : item.key.toString(),
    }));
  }
}

const mapStateToProps = (state: RootState) => ({
  insurerName: selectSelectedOfferInsurerName(state),
  internalInstallmentsEstimation: selectSelectedOfferInternalInstallmentsEstimation(
    state
  ),
  merchCapPriceAddition: selectMerchCapPriceAddition(state),
  isPaymentMethodOnInstallmentsActive: selectIsPaymentMethodOnInstallmentsActive(
    state
  ),
  priceBeforeRecalculation: selectPriceBeforeRecalculation(state),
  selectedOffer: selectSelectedOffer(state),
});

export default connect(mapStateToProps)(LargeRadioGroup);
